import {
  ElementRef,
  EventEmitter,
  Injectable,
  Injector,
  OnDestroy,
  Renderer2,
  RendererFactory2,
} from '@angular/core';

import { ClipboardService, TranslationService } from '@alfresco/adf-core';

import { Store } from '@ngrx/store';
import { TranslateParser } from '@ngx-translate/core';
import {
  Observable,
  Subject,
  from,
  BehaviorSubject,
  ReplaySubject,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import * as _ from 'lodash';

import UnnumRefTemplate from '../../assets/template/citations.template.json';
import { CommonConstants } from '../shared';
import { CitationScrollProperties } from '../types';
import { CKEditor5 } from '../components/ckeditor';
import {
  CitationUnnumberedRefModel,
  CitationRefModel,
  CitationPostAPIRequestModel,
  CitationObjectModel,
  CitationReferenceDocumentModel,
  CitationCopyRefModel,
} from '../model/citation.model';
import {
  CommonTarget,
  CommentTypeKeys,
  CommentTypes,
  CommentCategoryEnum,
  ISuggestion,
} from '../model/custom-models';
import { CitationDecoratorModel } from '../model/plugin-decorator.model';
import { IKeyWord } from '../store/states';
import { WINDOW_TOKEN } from '../components/shared/window.token';
import {
  SetLatestEditorContentAction,
  SnackbarErrorAction,
} from '../store/actions/app.custom-actions';
import { getAllKeyword } from '../store/selectors/app.custom-selectors';
import { AppStore } from '../store/states/app.state';
import { WkInsertDocumentLinkService } from './wk-link-document.service';
import {
  CitationModel,
  FragmentLinks,
  ICitation,
  ICommentThread,
  NumberedCitation,
  UnnumberedCitation,
} from '../modules/wk-document-decorator/models';
import { setBaseHref } from '../modules/wk-document-decorator/state/decorators.actions';
import {
  getBaseHrefForLinkPlugin,
  getBaseHrefId,
  getBaseHrefPathUrl,
} from '../modules/wk-document-decorator/state/selectors/get.basehref';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import { SanitizeService } from './sanitize.service';

export enum ReferenceTypes {
  NumberedRef = 'REFERENCES',
  UnnumberedRef = 'UNNUMBERED_REFERENCES',
}

export enum RHPLabels {
  CONTENTDETAILS,
  CROSSLINKS,
  COMMENTS,
}

const {
  CK_PLUGINS: { COMMENTS_REPOSITORY, TRACK_CHANGES_EDITING },
  comments: { dataComment },
  RELEASE_NOTES_TAG_NAME,
  SKOS_EDITORIAL_NOTE,
  ID_ATTR,
  rel,
} = CommonConstants;

export interface TrackChangesandCommentsInterface {
  suggestionsData: ISuggestion[];
  commentThreadsData: object[];
}
@Injectable({
  providedIn: 'root',
})
export class CitationService implements OnDestroy {
  triggerReOrderInDoc = new Subject<any>();
  tempRefArray = [];
  generateCustomGuid = new Subject<void>();
  getCustomGuid: string;
  existingGuid: string;
  refCountDeletion: boolean;
  deleteCurrentSelectedRefs = new Subject<any>();
  subsequentRefIdForDeletion: string;
  firstCitedRefIdForDeletion: string;
  triggerCitationReOrderInEditorComp = new Subject<any>();
  isMultipleRefsDeletion: boolean;
  multipleRefsToDelete: {
    id: string;
    subsequentRefID: string;
    citationCount: string;
    citationsTotal: number;
  }[];
  citationsPanelReOrderData = new Subject<any>();
  selectedRefId = new Subject<any>();
  parser = new DOMParser();
  citationEditorData: Document;
  notifyEditorCompOnCitationData = new Subject<boolean>();
  editorDataReady = new Subject<string>();
  updatedCitation: CitationObjectModel = {
    references: [],
    unnumbered_references: [],
  };
  closeMiniEditor = new EventEmitter<any>();
  notifyDeleteCitationsEvent = new EventEmitter<any>();
  additionalAttributesForDocReference = new Subject<object>();
  selectedCitationDoc = new Subject<CitationScrollProperties>();
  isDeleteCitationClicked = new EventEmitter<boolean>();
  notifyInfoDrawerStatus = new BehaviorSubject<boolean>(true);
  selectedIdCommentDoc = new ReplaySubject<string>(1);
  selectedSidebarCommentId = new Subject<any>();
  isCommentReadyForFocus = new BehaviorSubject<boolean>(false);
  idToFocus = new BehaviorSubject<any>('');
  trackChangesClickedInMiniEditor = new Subject<void>();
  selectedTrackChangeId = new BehaviorSubject<string | null>(null);
  sidebarModeChanged = new BehaviorSubject<string>('Wide sidebar'); // BehaviorSubject to emit when sidebar mode changes (with initial value)
  miniEditorInstance: CKEditor5.Editor; // Reference to mini editor instance
  miniEditorInstances: CKEditor5.Editor[] = []; // Active mini editor instances
  activeMiniEditorByCitationType: {
    [ReferenceTypes.NumberedRef]: CKEditor5.Editor;
    [ReferenceTypes.UnnumberedRef]: CKEditor5.Editor;
  } = {
    [ReferenceTypes.NumberedRef]: null,
    [ReferenceTypes.UnnumberedRef]: null,
  };
  ckeditorInstance = false;
  selectedIdForDeletion: string;
  editReferences: boolean;
  editUnnumberedReferences: boolean;
  isExistingRef: any;
  private readonly editable = true;
  nodeId: string;
  currentEditorData: string;
  contentType = ['application/json'];
  citationAPIRequest: CitationPostAPIRequestModel;
  isCurrentCitationsUpdated = false;
  shouldSaveContentData = false;
  private readonly changeCitationTab = new BehaviorSubject<number>(0);
  changePanel$ = this.changeCitationTab.asObservable();
  private readonly tabDisable = new Subject<boolean>();
  disableTab$ = this.tabDisable.asObservable();
  panelDisable = new Subject<boolean>();
  showCitationActionIcons = true;
  citationRefAttributes: CitationReferenceDocumentModel;
  citationReOrderArray = [];
  tempCitationReferences: CitationRefModel[];
  documentCitationRefIds: string[];
  public createCitationRef = new Subject<boolean>();
  totalCitedCount: number;
  citationRefObj: CitationRefModel;
  citationRefObjMap = new Map<string, CitationRefModel>();
  citationRefAdditionalAttributes: CitationDecoratorModel = {};
  additionalAttrForEachRef;
  copyReference = new EventEmitter<CitationCopyRefModel>();
  citationString;
  currentSelectedCitationDoc = '';
  isCitationCopied = false;
  editNumberRef = false;
  editUnnumRef = false;
  shouldDisableCitation;
  deleteCitationIndex;
  itemToReOrder;
  deleteCitationEditType;
  documentRefIds;
  citationEdit;
  datatoBeUpdated;
  commentsData = new BehaviorSubject<any>({
    commentThreadsData: [],
    suggestionsData: [],
    descriptionOfSuggestions: [],
  });
  isOpenCommentsTab = new BehaviorSubject<boolean>(false);

  triggerDocumentSaveAndCheckin = new Subject<boolean>();
  disableSaveButton = new Subject<boolean>();
  disableComments = new Subject<boolean>();
  isCommentsTabLoaded = new BehaviorSubject<boolean>(false);
  commentStatusThreadId = { threadId: '', status: '' };
  deleteComments = new Subject<boolean>();
  acceptRejectSuggestions = new BehaviorSubject<string>('');
  // ENHANCEMENT: Signal when user accepts/discards selected (individual) track change suggestions
  acceptRejectSelectedSuggestions = new BehaviorSubject<string>('');
  setRightClickEventDetails = new BehaviorSubject<string>('');
  ckeditorInstanceFromEditorComp: CKEditor5.Editor;
  // BUGFIX-001: Separate reference to RHP editor to avoid mixing suggestions from main editor
  rhpEditorInstance: CKEditor5.Editor;
  citationsForSplitMode = {
    references: [],
    unnumbered_references: [],
  } as CitationObjectModel;
  suggestionAttributes = [
    `[${CommonConstants.trackchangesMarkup.SUGGESTION}]`,
    `[${CommonConstants.trackchangesMarkup.SUGGESTION_END_AFTER}]`,
    `[${CommonConstants.trackchangesMarkup.SUGGESTION_END_BEFORE}]`,
    `[${CommonConstants.trackchangesMarkup.SUGGESTION_START_AFTER}]`,
    `[${CommonConstants.trackchangesMarkup.SUGGESTION_START_BEFORE}]`,
  ];
  notifyUnNumRefCreation = new Subject<UnnumberedCitation[]>();
  contextMenuLength: number;
  public loadCitationComponent$ = new BehaviorSubject<boolean>(false);
  private readonly renderer: Renderer2;
  private readonly globalWindow: Window;
  private readonly onDestroy$: Subject<boolean> = new Subject<boolean>();
  private readonly translateParser: TranslateParser;
  keywords: IKeyWord[] = [];
  headerElement: Node;
  private readonly keywords$: Observable<IKeyWord[]> =
    this.store.select(getAllKeyword);
  private readonly baseHrefForLinkPlugin$: Observable<string> =
    this.store.select(getBaseHrefForLinkPlugin);
  private readonly baseHrefPathUrl$: Observable<string> =
    this.store.select(getBaseHrefPathUrl);
  private readonly baseHrefId$: Observable<string> =
    this.store.select(getBaseHrefId);

  public isInlineToggledSubject = new BehaviorSubject<boolean>(false);
  public isInlineToggled$ = this.isInlineToggledSubject.asObservable();
  public activeAnnotationListener = new Subject<number>();
  public saveDocAndGenerateUMC = new Subject<void>();
  public triggerSaveInEditorComp = new Subject<void>();
  public editorContentChanged = new Subject<void>();
  public rhpTabChanged = new Subject<void>();
  // BUGFIX-001: Signal when a mini-editor is registered for a specific citation type
  public miniEditorRegisteredForType$ = new Subject<string>();
  private baseHrefForLinkPlugin: string;
  private baseHrefPathUrl: string;
  private baseHrefId: string;
  constructor(
    private readonly apiService: WkAlfrescoApiService,
    private readonly rendererFactory: RendererFactory2,
    private readonly injector: Injector,
    private readonly insertDocumentLinkService: WkInsertDocumentLinkService,
    private readonly store: Store<AppStore>,
    private readonly translationService: TranslationService,
    private readonly clipboardService: ClipboardService,
    private readonly sanitizeService: SanitizeService
  ) {
    this.rendererFactory =
      this.injector.get<RendererFactory2>(RendererFactory2);
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.globalWindow = this.injector.get<Window>(WINDOW_TOKEN);
    this.translationService =
      this.injector.get<TranslationService>(TranslationService);
    this.clipboardService =
      this.injector.get<ClipboardService>(ClipboardService);
    this.translateParser = this.injector.get<TranslateParser>(TranslateParser);
    this.keywords$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((keywords: IKeyWord[]) => {
        this.keywords = keywords;
      });
    this.baseHrefForLinkPlugin$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((baseHrefForLinkPlugin = '') => {
        this.baseHrefForLinkPlugin = baseHrefForLinkPlugin;
      });
    this.baseHrefPathUrl$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((baseHrefPathUrl = '') => {
        this.baseHrefPathUrl = baseHrefPathUrl;
      });
    this.baseHrefId$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((baseHrefId = '') => {
        this.baseHrefId = baseHrefId;
      });
  }

  private _currentCitations = {
    references: [],
    unnumbered_references: [],
  } as CitationObjectModel;

  newCitations = {
    references: [],
    unnumbered_references: [],
  };
  private _addedCitationData: string;
  private _citationEditType: string;
  private _resource: string;
  private _about: string;
  private _miniEditorCoords: {
    left: number;
    top: number;
    clientX: number;
    marginLeft: string;
    marginRight: string;
    offsetHeight: number;
  };
  idToBeDeleted = new EventEmitter<string>();
  itemsToBeDeletedCount = 0;
  chevronNav = new Subject<any>();
  editorData: string;
  counter = {};
  countForEachCitations = {};
  citationsCounter = new Subject<any>();
  public get resource() {
    return this._resource;
  }

  public set resource(resourceString: string) {
    this._resource = resourceString;
  }

  public get miniEditorCoords() {
    return this._miniEditorCoords;
  }

  public set miniEditorCoords(data: {
    left: number;
    top: number;
    clientX: number;
    marginLeft: string;
    marginRight: string;
    offsetHeight: number;
  }) {
    this._miniEditorCoords = {
      left: data.left,
      top: data.top,
      clientX: data.clientX,
      marginLeft: data.marginLeft,
      marginRight: data.marginRight,
      offsetHeight: data.offsetHeight,
    };
  }

  public get about() {
    return this._about;
  }

  public set about(aboutString: string) {
    this._about = aboutString;
  }

  public get currentCitations() {
    return this._currentCitations;
  }

  public set currentCitations(citations) {
    this._currentCitations = citations;
  }

  public get citationEditType() {
    return this._citationEditType;
  }

  public set citationEditType(type: string) {
    this._citationEditType = type;
  }

  public set addedCitationData(citationData: string) {
    this._addedCitationData = !this.editReferences
      ? citationData
          .replace(/&nbsp;/gi, ' ')
          .replace(/<\/p><p>/gi, ' ')
          .replace(/<\/?p>/gi, '')
          .replace(/(<p[^>]+?>|<p>|<\/p>)/g, ' ')
          .trim()
      : citationData.replace(/&nbsp;/gi, ' ');
  }

  changeSidePanelTabTo(index: number, disableTab = false) {
    this.changeCitationTab.next(index);
    if (disableTab) {
      this.disableSidePanelTabs(true, index);
    }
  }

  disableSidePanelTabs(value: boolean, index = 0) {
    if (index !== 2) {
      this.tabDisable.next(value);
    } else {
      this.disableComments.next(value);
    }
  }

  /**
   *
   * @param guidToRemove
   * @returns id of the span tag from citationString of unnumbered ref
   * @returns null if id attribute is missing
   */
  getIdFromCitationString(guidToRemove): string {
    const parsedCitationString = this.parser.parseFromString(
      guidToRemove.citationString,
      'text/html'
    );
    return (
      parsedCitationString.body.querySelector('[id]').getAttribute('id') || null
    );
  }

  saveAndCheckinAsObservable() {
    return this.triggerDocumentSaveAndCheckin.asObservable();
  }

  disableSaveButtonAsObservable() {
    return this.disableSaveButton.asObservable();
  }

  switchTab() {
    this.shouldDisableCitation =
      this._currentCitations.unnumbered_references?.length === 0 &&
      this._currentCitations.references?.length === 0 &&
      this.keywords?.length === 0;
    if (this.changeCitationTab.value !== 2) {
      if (this.shouldDisableCitation) {
        this.changeCitationTab.next(0);
        this.tabDisable.next(this.shouldDisableCitation);
      } else {
        this.changeCitationTab.next(1);
        this.tabDisable.next(this.shouldDisableCitation);
      }
    } else if (this.shouldDisableCitation) {
      this.tabDisable.next(this.shouldDisableCitation);
    }
  }

  createCitationRefInstance(
    id: string,
    { isExistingCitation, hasExistingCitationString, hasExistingCitationRefObj }
  ) {
    const citationRef = this.citationRefObjMap.get(id);
    const hasExistingCitation = isExistingCitation && hasExistingCitationRefObj;

    const citationString = hasExistingCitationString
      ? citationRef.citationString
      : this._addedCitationData;

    const resource = hasExistingCitation
      ? citationRef.resource
      : this.additionalAttrForEachRef?.resource || '';

    const about = hasExistingCitation
      ? citationRef.about
      : this.additionalAttrForEachRef?.about || '';

    this.citationRefObj = new CitationRefModel(
      id,
      citationString,
      this.editable,
      resource,
      about,
      []
    );
  }

  setCitationAdditionalAttribute(
    id: string,
    { isExistingCitation, hasExistingCitationRefObj }
  ) {
    this.additionalAttrForEachRef = JSON.parse(
      JSON.stringify(this.citationRefAdditionalAttributes)
    );

    let baseHrefId = this.baseHrefId;
    baseHrefId = baseHrefId ? baseHrefId.concat('#') : '';

    let baseHrefPathUrl = this.baseHrefPathUrl;
    baseHrefPathUrl = baseHrefPathUrl ? baseHrefPathUrl.concat('/') : '';

    this.additionalAttrForEachRef['about'] = isExistingCitation
      ? hasExistingCitationRefObj && this.citationRefObjMap.get(id).about
      : `${baseHrefPathUrl}${baseHrefId}${this.citationRefAttributes.id}`;

    if (!isExistingCitation) {
      this.createGuid();
    }
    this.additionalAttrForEachRef['resource'] = isExistingCitation
      ? hasExistingCitationRefObj && this.citationRefObjMap.get(id).resource
      : UnnumRefTemplate.NUM_REF_MARKER.RESOURCE + this.getCustomGuid;

    this.additionalAttrForEachRef['typeof'] =
      CommonConstants.pciCoreBibliographicResource;
    this.additionalAttributesForDocReference.next(
      this.additionalAttrForEachRef
    );
  }

  checkNestedContent(id: string) {
    const tag = this.parser
      .parseFromString(
        this.citationRefObjMap.get(id)?.citationString,
        'text/html'
      )
      .body.querySelector('p');

    if (Boolean(tag)) {
      this.citationRefObjMap.set(id, {
        ...this.citationRefObjMap.get(id),
        citationString: tag.innerHTML,
      });
    }
  }

  citationObjCreation(refType: string, isExistingCitation = false) {
    if (refType === ReferenceTypes.NumberedRef) {
      this.citationRefAttributes = {
        id: uuidv4(),
      };
      const { id } = this.citationRefAttributes;
      const hasExistingCitationRefObj = Boolean(this.citationRefObjMap.get(id));
      const hasExistingCitationString =
        isExistingCitation &&
        Boolean(this.citationRefObjMap.get(id)?.citationString);

      this.setCitationAdditionalAttribute(id, {
        isExistingCitation,
        hasExistingCitationRefObj,
      });

      if (hasExistingCitationString) {
        this.checkNestedContent(id);
        this._addedCitationData = this.citationRefObjMap.get(id).citationString;
      }

      this.createCitationRefInstance(id, {
        isExistingCitation,
        hasExistingCitationString,
        hasExistingCitationRefObj,
      });
      if (!isExistingCitation) {
        this.citationRefObjMap.set(id, {
          ...this.citationRefObj,
          citationId: id,
        });
      }
    }
    this.isCurrentCitationsUpdated = true;
  }

  createUnNumRef(): CitationUnnumberedRefModel {
    const guidBR = uuidv4();
    const guidx = uuidv4();
    this.createUnNumCitationMarker(guidBR);
    let unNumString = this.generateUnNumRef(guidBR, guidx);
    unNumString = this.parser
      .parseFromString(unNumString, 'text/html')
      .body.innerHTML.toString();

    return new CitationUnnumberedRefModel(
      uuidv4(),
      unNumString,
      this.editable,
      guidBR
    );
  }

  createUnNumCitationMarker(guidBR: string) {
    if (this.ckeditorInstanceFromEditorComp) {
      if (!this.headerElement) {
        const range = this.ckeditorInstanceFromEditorComp.model.createRangeIn(
          this.ckeditorInstanceFromEditorComp.model.document.getRoot()
        );
        for (const item of range.getItems({ ignoreElementEnd: true })) {
          if (item.name === 'header') {
            this.headerElement = item;
            break;
          }
        }
      }

      const unNumMarkerAttrs = { ...UnnumRefTemplate.UNNUM_REF_MARKER };
      unNumMarkerAttrs['about'] = this.getBaseHref();
      unNumMarkerAttrs.resource = `${UnnumRefTemplate.UNNUM_REF_MARKER.resource}${guidBR}`;
      this.ckeditorInstanceFromEditorComp.model.change((writer) => {
        writer.appendElement(
          CommonConstants.spanTagName,
          unNumMarkerAttrs,
          this.headerElement
        );
      });
    }
  }

  citationReOrder() {
    return map(
      filter(
        this.citationReOrderArray,
        (item) => !item?.getAttribute(CommonConstants.dataParentCitation)
      ),
      (item) => item.id
    );
  }

  reOrderReferencesArray(references: ICitation[], data?: any) {
    this.tempCitationReferences = [];
    this.documentCitationRefIds = data ? data : this.citationReOrder();
    forEach(references, (reference: CitationRefModel) => {
      this.tempCitationReferences.splice(
        this.documentCitationRefIds.indexOf(reference.citationId),
        0,
        reference
      );
    });
  }

  reOrderReferencesInCitationsPanel(references: ICitation[]) {
    this.tempRefArray = [];
    this.documentRefIds = this.citationReOrder();
    for (const id of this.documentRefIds) {
      this.itemToReOrder = filter(
        references,
        (reference) => reference.citationId === id
      );
      if (this.itemToReOrder[0]) {
        this.tempRefArray.push(this.itemToReOrder[0]);
      }
    }
  }

  createGuid() {
    this.generateCustomGuid.next();
  }

  appendCitations(
    editorContent: string,
    references: ICitation[],
    isSplitMode = false
  ) {
    this.citationEditorData = this.parser.parseFromString(
      editorContent,
      'text/html'
    );
    const citationsToCheckFor = isSplitMode
      ? this.citationsForSplitMode
      : { references };
    if (citationsToCheckFor.references?.length) {
      citationsToCheckFor.references.forEach(
        (eachReference, eachReferenceIndex) => {
          eachReference.referencedBy.forEach((item: string, index: number) => {
            const element = this.citationEditorData.body.querySelector(
              `[id="${item}"]`
            );
            if (element) {
              element.setAttribute(
                'class',
                `ref ${CommonConstants.numberedCitation}`
              );
              element.setAttribute(
                CommonConstants.dataCitationCount,
                `${eachReferenceIndex + 1}`
              );
              element.setAttribute('contenteditable', 'false');
              index > 0 &&
                element.setAttribute(
                  'data-parentcitation',
                  eachReference.referencedBy[0]
                );
            }
          });
        }
      );
    }
    return Promise.resolve(this.citationEditorData.body.innerHTML.toString());
  }

  getHiddenContentPart(content: string): string {
    const bodyTagStartIndex = content.indexOf('<body');
    if (bodyTagStartIndex > -1) {
      return content.slice(0, bodyTagStartIndex);
    } else {
      return '';
    }
  }

  citationsPrerequisitesPostChanges() {
    this.switchTab();
  }

  updateCommentThread(
    trackChangesAndCommentsData: TrackChangesandCommentsInterface
  ) {
    // Updates status draft/approve/reject
    return map(trackChangesAndCommentsData.commentThreadsData, (threads) => {
      if (
        threads[CommentTypeKeys.threadId] ===
        this.commentStatusThreadId.threadId
      ) {
        threads[CommentTypeKeys.commentKey].forEach((element) => {
          if (
            (Object.values(CommentTypes) as string[]).includes(
              this.commentStatusThreadId.status
            )
          ) {
            element.attributes.status = this.commentStatusThreadId.status;
          } else if (
            element.attributes.commentType !== CommentCategoryEnum.RELEASE_NOTES
          ) {
            element.attributes.status = !(
              Object.values(CommentTypes) as string[]
            ).includes(element.attributes.status)
              ? CommentTypeKeys.draftStatus
              : element.attributes.status;
          }
        });
      }
      return threads;
    });
  }

  createandUpdateCitations(
    editorData: string,
    trackChangesAndCommentsData: TrackChangesandCommentsInterface,
    isCalledByMerge = false,
    citations: CitationModel = undefined,
    fragmentLinks: FragmentLinks = undefined
  ): Observable<any> {
    citations = this.sanitizeService.sanitizeCitation(citations);
    this.updatedCitation.references = citations?.references as any;
    this.updatedCitation.unnumbered_references =
      citations?.unnumbered_references as any;
    this.newCitations = { ...this.updatedCitation };
    editorData = this.sanitizeService
      .sanitizeHtml(editorData)
      .replace(/&nbsp;<\/span>/g, ' </span>')
      .replace(/&nbsp;<\/div>/g, ' </div>');
    this.citationAPIRequest = isCalledByMerge
      ? new CitationPostAPIRequestModel(editorData, undefined)
      : new CitationPostAPIRequestModel(
          this.shouldSaveContentData ? editorData : undefined,
          citations as CitationObjectModel
        );

    this.citationAPIRequest['suggestions'] =
      trackChangesAndCommentsData.suggestionsData;
    this.citationAPIRequest['commentThreads'] = this.updateCommentThread(
      trackChangesAndCommentsData
    );
    this.citationAPIRequest['keyWords'] = this.keywords;
    fragmentLinks && (this.citationAPIRequest['fragmentLinks'] = fragmentLinks);
    this.currentEditorData = editorData;
    this.commentStatusThreadId = { threadId: '', status: '' };
    this.store.dispatch(new SetLatestEditorContentAction(editorData));
    return from(
      this.apiService
        .callContentClientCustomApi({
          path: `alfresco/s/wk/nodes/${this.nodeId}/ckeditor`,
          httpMethod: 'POST',
          bodyParam: this.citationAPIRequest,
          contentTypes: this.contentType,
          accepts: this.contentType,
        })
        .catch((error) => {
          this.store.dispatch(new SnackbarErrorAction(error.message));
          if (error.status !== 404) {
            throw Error(error);
          }
        })
    );
  }

  private redirectToLinkAddress(linkAddress, linkElement) {
    this.insertDocumentLinkService
      .getNodeOfLinkedDoc(linkAddress)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data) => {
          const docId = data.list.entries[0].entry.id;
          this.globalWindow.open(`#/dashboard/preview/${docId}`, '_blank');
          this.renderer.setAttribute(
            linkElement,
            'redirectLink',
            `#/dashboard/preview/${docId}`
          );
          this.onDestroy$.next(false);
        },
        error: (error) => {
          this.store.dispatch(new SnackbarErrorAction(error.message));
        },
      });
  }

  routeToLink(elementRef: ElementRef, event: any) {
    const linkAddress = event?.linkAttributes
      ? event.linkAttributes.linkAddress
      : event.target.getAttribute('resource');
    const linkElement = elementRef.nativeElement.querySelector(
      `[resource='${linkAddress}']`
    );
    const typeOf = linkElement?.getAttribute('typeof');
    if (typeOf === CommonConstants.pciCoreWebResource) {
      this.globalWindow.open(
        `${linkElement.getAttribute('resource')}`,
        '_blank'
      );
    } else {
      linkElement?.getAttribute('redirectLink')
        ? this.globalWindow.open(
            `${linkElement.getAttribute('redirectLink')}`,
            '_blank'
          )
        : this.redirectToLinkAddress(linkAddress, linkElement);
    }
  }

  /**
   * Description: Copies the target text from the event recieved to the clipborad
   * Params: unumberered reference object
   */
  copyContentToClipboard(unnumberedCopyReference: CommonTarget): void {
    const message = this.translate(
      'APP.CITATIONS.COPY_UNNUMBERED_NUMBERED_REFS_SUCCESS_MESSAGE'
    );
    this.clipboardService.copyContentToClipboard(
      unnumberedCopyReference.innerText,
      message
    );
  }

  /**
   * Description: Calls translationService instant method to fetch the success message
   * Params: message to show, interPolate params
   */
  private translate(message: string, params?: Object): string {
    return this.translationService.instant(message, params);
  }

  private generateUnNumRef(guidBR: string, guidx: string): string {
    return this.translateParser.interpolate(
      UnnumRefTemplate.UNNUM_REF_TEMPLATE,
      {
        guidBR,
        guidx,
        citationString: this._addedCitationData.replace(
          /contenteditable='true'|contenteditable="true"|contenteditable=\"true\"/gi,
          ''
        ),
        baseHREF: this.getBaseHref(),
      }
    );
  }

  private getBaseHref() {
    if (!this.baseHrefForLinkPlugin) {
      this.assignBaseHrefFromDocument();
    }
    return this.baseHrefForLinkPlugin || '';
  }

  private assignBaseHrefFromDocument(): void {
    const baseHref = RegExp(/<base[^>]*>/).exec(this.currentEditorData);
    const baseHrefUrl = baseHref[0].split('"');
    const baseHrefPathUrl = baseHrefUrl[1].substring(
      0,
      baseHrefUrl[1].lastIndexOf('/')
    );
    const baseHrefId = baseHrefUrl[1].substring(
      baseHrefUrl[1].lastIndexOf('/') + 1,
      baseHrefUrl[1].length
    );

    this.store.dispatch(
      setBaseHref({
        baseHrefForLinkPlugin: baseHref[0].split('"')[1],
        baseHrefPathUrl,
        baseHrefId,
      })
    );
  }

  public createNumberRefObj() {
    const { citationId, citationString, resource, about } = this.citationRefObj;
    const id = uuidv4();
    const _citationString = this.translateParser.interpolate(
      UnnumRefTemplate.RDFA_NUM_REF_TEMPLATE,
      {
        content: citationString.replace(
          /contenteditable='true'|contenteditable="true"|contenteditable=\"true\"/gi,
          ''
        ),
        referencedBy: [citationId],
        externalCitationGuid: resource.split('/').pop(),
        internalCitationGuid: id,
        about: about.split('#')[0],
      }
    );
    return {
      citationId: id,
      citationString: _citationString,
      editable: true,
      isCitationDeleted: false,
      referencedBy: [citationId],
    };
  }

  /**
   * Description: Returns the opened side panel
   * @public
   * @returns { number }
   */
  public getOpenedSidePanel(): number {
    return this.changeCitationTab.value;
  }

  /*
   * When multiple inline images are added inside a span tag as parent in drug template, Upon uploading this document ckeditor creates a p tag inside span
   * Sample <span> test<span class="ck-span">..</span> test2 <span class="ck-span">..</span>  test3 <span class="ck-span">..</span>
   *  This function unwraps the incorrect p tag and puts the children back into its original state
   * @param editorInstance Editor instance from preview and edit mode
   */
  public updateInlineImageMarkupsInsideSpan(editorInstance): void {
    setTimeout(() => {
      try {
        const range = editorInstance?.model?.createRangeIn(
          editorInstance?.model?.document?.getRoot()
        );
        const rangeItems = range?.getItems?.({ ignoreElementEnd: true });
        if (rangeItems && Symbol.iterator in Object(rangeItems)) {
          for (const item of rangeItems) {
            const mappedEle =
              editorInstance?.editing?.mapper?.toViewElement(item);
            const parentName = mappedEle?.parent?.name;

            if (
              mappedEle?.name === CommonConstants.spanTagName &&
              [
                CommonConstants.spanTagName,
                CommonConstants.paragraphTagName,
                'p',
              ].includes(parentName) &&
              mappedEle.getAttribute('class') ===
                CommonConstants.inlineMediaClass
            ) {
              const currentChildrenOfInlineImageSpan = [...item.getChildren()];
              this.appendPTagChild(
                editorInstance,
                currentChildrenOfInlineImageSpan,
                item
              );
            }
          }
        }
      } catch (err) {
        console.error(
          'Error while updating inline image markups inside span:',
          err
        );
      }
    }, 150);
  }

  private appendPTagChild(
    editorInstance,
    currentChildrenOfInlineImageSpan,
    item
  ): void {
    editorInstance?.model?.change((writer) => {
      forEach(currentChildrenOfInlineImageSpan, (child) => {
        /**
         * If p tag is found, all children inside p tag will be appended back to its original structure
         */
        if (child.name === CommonConstants.paragraphTagName) {
          const deepClonedPtag = writer.cloneElement(child);

          writer.remove(child);

          forEach([...deepClonedPtag.getChildren()], (deepClonedPtagChild) => {
            writer.append(deepClonedPtagChild, item.parent);
          });
        }
      });
    });
  }

  public syncCitationFromDocument(
    editorData,
    references: ICitation[]
  ): ICitation[] {
    const availableCitationsInDoc = this.parser
      .parseFromString(editorData, 'text/html')
      .querySelectorAll(`.${CommonConstants.numberedCitation}`);

    const groupedRefs = {};

    _.forEach(availableCitationsInDoc, (ref) => {
      const id = ref.id;
      const parent = ref.getAttribute(
        CommonConstants.parentCitationPlaceholder
      );

      if (!parent) {
        // This is a root reference
        groupedRefs[id] = [id];
      }

      if (parent && groupedRefs[parent]) {
        groupedRefs[parent].push(id);
      }
    });

    const updatedRefsWithReferencedBy = _.map(
      _.cloneDeep(references),
      (ref: NumberedCitation) => {
        groupedRefs[ref.referencedBy[0]] &&
          (ref.referencedBy = groupedRefs[ref.referencedBy[0]]);
        return ref;
      }
    );

    return _.filter(updatedRefsWithReferencedBy, (ref: NumberedCitation) => {
      return groupedRefs[ref.referencedBy[0]] && ref;
    });
  }

  /**
   * Fetches track changes suggestions in repository
   *
   * @returns {ISuggestion[]}
   */
  public getTrackChangesInRepository(): ISuggestion[] {
    return this.ckeditorInstanceFromEditorComp?.plugins
      ?.get(TRACK_CHANGES_EDITING)
      ?.getSuggestions({ skipNotAttached: true, toJSON: true });
  }

  /**
   * Fetches comment threads in repository based on thread type
   *
   * @param {string} threadType - 'comment' or 'releasenotes'
   * @returns {ICommentThread[]}
   */
  public getCommentsInRepository(threadType: string): ICommentThread[] {
    if (!this.ckeditorInstanceFromEditorComp) {
      return [];
    }
    const { editing, plugins } = this.ckeditorInstanceFromEditorComp;
    const commentRepository = plugins.get(COMMENTS_REPOSITORY);
    const commentThreads: ICommentThread[] =
      commentRepository.getCommentThreads({
        toJSON: true,
      });
    return _.filter(commentThreads, (thread: ICommentThread): boolean => {
      const domRoot = editing.view.domRoots.get('main');
      const threadId = thread.threadId;
      const releaseNoteSelector = `[${ID_ATTR}="${threadId}"][${rel}^="${SKOS_EDITORIAL_NOTE}"]`;
      const commentSelector = `[${dataComment}="${threadId}"]:not([${rel}^="${SKOS_EDITORIAL_NOTE}"])`;
      const releaseNoteCount =
        domRoot.querySelectorAll(releaseNoteSelector).length;
      const commentCount = domRoot.querySelectorAll(commentSelector).length;
      return threadType === RELEASE_NOTES_TAG_NAME
        ? releaseNoteCount > 0
        : commentCount > 0;
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }
}
